import { TFunction } from 'i18next';
import { Booking } from '../pages/rentals/Bookings/types';
import { RentalBookingDetails } from '../types/bookingCreation';
import { store } from '../store/store';
import { bookingApiSlice } from '../api/booking/bookingApiSlice';
import { FREE_PAYMENT_STATUS, isCoveredByOrganization, isFreeByPriceBooking, paymentBadgeStatus } from './bookingCoverage';
// The escaping — including the CSV-injection guard (CWE-1236) — lives in its
// own import-free module so it can actually be unit-tested; this file reaches
// the Redux store and through it Auth0, which no test environment survives.
import { escapeCSVField } from './csvFieldEscape';
import { downloadCsv } from './csvDownload';
import { stationCalendarDay } from './stationTime';
import { csvRoomColumnsOf } from './csvRoomColumns';

export interface CsvExportOptions {
    tenant: string;
    serviceFilter?: string;
    dateFrom?: string;
    dateTo?: string;
}

const BATCH_SIZE = 20;

interface FetchDetailsResult {
    detailsMap: Map<string, RentalBookingDetails>;
    failedCount: number;
}

const fetchSingleDetail = async (
    bookingId: string,
): Promise<{ bookingId: string, data?: RentalBookingDetails }> => {
    const result = await store.dispatch(
        bookingApiSlice.endpoints.getBookingDetails.initiate(
            { orderId: bookingId },
            { forceRefetch: false },
        ),
    );
    return { bookingId, data: result.data };
};

const fetchBookingDetails = async (
    bookings: Booking[],
): Promise<FetchDetailsResult> => {
    const detailsMap = new Map<string, RentalBookingDetails>();
    const failedBookingIds: string[] = [];

    for (let i = 0; i < bookings.length; i += BATCH_SIZE) {
        const batch = bookings.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(
            batch.map((booking) => fetchSingleDetail(booking.bookingId)),
        );

        results.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value.data) {
                detailsMap.set(batch[index].bookingId, result.value.data);
            } else {
                failedBookingIds.push(batch[index].bookingId);
            }
        });
    }

    // Retry failed requests individually, bypassing cache
    for (const bookingId of failedBookingIds) {
        try {
            const result = await store.dispatch(
                bookingApiSlice.endpoints.getBookingDetails.initiate(
                    { orderId: bookingId },
                    { forceRefetch: true },
                ),
            );
            if (result.data) {
                detailsMap.set(bookingId, result.data);
            }
        } catch {
            // Still failed after retry — will be counted below
        }
    }

    const failedCount = bookings.length - detailsMap.size;
    return { detailsMap, failedCount };
};

const formatTotal = (total: number | undefined, language: string): string => {
    if (total === undefined) return '';
    return new Intl.NumberFormat(language === 'de' ? 'de-DE' : 'en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(total);
};

const filterByDateRange = (
    bookings: Booking[],
    dateFrom?: string,
    dateTo?: string,
): Booking[] => {
    if (!dateFrom && !dateTo) return bookings;

    return bookings.filter((booking) => {
        // The station day the booking starts on — not the UTC date of the ISO
        // string, which put an apartment checking in on the 1st (stored at
        // local midnight, 22:00Z the day before) into the previous month.
        const startDate = booking.startDate ? stationCalendarDay(booking.startDate) : '';
        if (dateFrom && startDate < dateFrom) return false;
        if (dateTo && startDate > dateTo) return false;
        return true;
    });
};

const buildFileName = (options: CsvExportOptions, language: string): string => {
    const today = stationCalendarDay(new Date());
    const tenant = options.tenant.toLowerCase().replace(/\s+/g, '-');
    const label = language === 'de' ? 'buchungen' : 'bookings';
    const parts = [tenant];

    if (options.serviceFilter) {
        parts.push(options.serviceFilter);
    }

    parts.push(label, today);
    return parts.join('-') + '.csv';
};

export interface CsvExportResult {
    failedDetailCount: number;
    totalCount: number;
}


/** A covered booking is never invoiced to its booker: "n.z.", not "fehlt". */
// No invoice is addressed for a covered booking or one free by price (PO,
// 2026-10-23), so a missing number there is "not applicable", not "missing".
const invoiceNumberCell = (
    booking: Booking, details: RentalBookingDetails | undefined, na: string, nv: string,
): string => {
    if (details?.invoiceNumber) return details.invoiceNumber;
    return isCoveredByOrganization(booking) || isFreeByPriceBooking(details) ? na : nv;
};

/** The event type cell, where the column applies. */
const eventTypeCell = (details: RentalBookingDetails | undefined, t: TFunction, nv: string): string =>
    details?.eventType
        ? t(`booking_details.event_type.${details.eventType}`, { defaultValue: details.eventType })
        : nv;

/** The guest count cell, where the column applies: a count of 0 or none is missing. */
const numberOfGuestsCell = (details: RentalBookingDetails | undefined, nv: string): string =>
    details?.numberOfGuests != null && details.numberOfGuests > 0
        ? details.numberOfGuests.toString()
        : nv;

/**
 * The payment status cell. A covered booking (#1926) is unpaid on purpose and
 * exporting it as "unpaid" would contradict the bookings table — the export is
 * what gets forwarded to whoever chases payments. A booking free by price (PO,
 * 2026-10-23) reads "Kostenfrei", as on its detail page; that flag comes with
 * the booking details.
 */
const paymentStatusCell = (
    booking: Booking,
    details: RentalBookingDetails | undefined,
    t: TFunction,
    nv: string,
): string => {
    if (isCoveredByOrganization(booking)) return t('bookings.bookings_table.covered_by_org');
    if (!booking.paymentStatus) return nv;
    if (paymentBadgeStatus(details, booking.paymentStatus) === FREE_PAYMENT_STATUS) {
        return t('booking_details.status.free');
    }
    return t(`booking_details.status.${booking.paymentStatus.toLowerCase()}`, { defaultValue: booking.paymentStatus });
};

/**
 * An amount cell, or the missing marker. The open amount of a covered booking
 * never reaches this: nothing is open there — the customer owes nothing
 * (ADR-0008 2026-09-23) — so the backend's total − paid is exported as "n.z.",
 * like the price summary, which names the organization instead.
 */
const formatOptionalTotal = (value: number | null | undefined, missing: string, language: string): string =>
    (value != null ? formatTotal(value, language) : missing);

export const exportBookingsToCSV = async (
    bookings: Booking[],
    t: TFunction,
    language: string,
    options: CsvExportOptions,
): Promise<CsvExportResult> => {
    const na = t('bookings.bookings_table.csv.na'); // not applicable (field doesn't exist for this use case)
    const nv = t('bookings.bookings_table.csv.nv'); // not available (field exists but data is missing)

    const filteredByDate = filterByDateRange(bookings, options.dateFrom, options.dateTo);
    const { detailsMap, failedCount } = await fetchBookingDetails(filteredByDate);

    const headers = [
        t('bookings.bookings_table.headers.booking_id'),
        t('bookings.bookings_table.csv.invoice_number'),
        t('bookings.bookings_table.headers.service'),
        t('bookings.bookings_table.csv.event_type'),
        t('bookings.bookings_table.headers.start_time'),
        t('bookings.bookings_table.headers.end_time'),
        t('bookings.bookings_table.headers.address'),
        t('bookings.bookings_table.headers.customer_name'),
        t('bookings.bookings_table.csv.email'),
        t('bookings.bookings_table.csv.phone'),
        t('bookings.bookings_table.csv.company'),
        t('bookings.bookings_table.csv.customer_address'),
        t('bookings.bookings_table.csv.locker_door'),
        t('bookings.bookings_table.csv.number_of_guests'),
        t('bookings.bookings_table.headers.booking_status'),
        t('bookings.bookings_table.headers.payment_status'),
        t('bookings.bookings_table.csv.total'),
        t('bookings.bookings_table.csv.amount_paid'),
        t('bookings.bookings_table.csv.balance'),
        t('bookings.bookings_table.csv.discount'),
        t('bookings.bookings_table.headers.issue'),
        t('bookings.bookings_table.csv.comment'),
        t('bookings.bookings_table.csv.created_at'),
    ];

    const rows = filteredByDate.map((booking) => {
        const details = detailsMap.get(booking.bookingId);

        const columns = csvRoomColumnsOf(booking.useCase);

        const address =
            columns.titleAsAddress
                ? (booking.langNames?.[language === 'de' ? 'de' : 'en']?.title ??
                  booking.assetName ?? nv)
                : (booking.address || nv);

        const serviceLabel = t(`service_type_selector.${booking.useCase}`, {
            defaultValue: booking.useCase,
        });

        const isRoom = columns.titleAsAddress;

        const email = details?.customerDetails?.email || booking.senderEmail || nv;
        const phone = details?.customerDetails?.phone || nv;
        const company = details?.customerDetails?.company || nv;
        const customerAddress = details?.customerDetails?.address || nv;

        const bookingStatus = t(
            `booking_details.status.${booking.bookingStatus.toLowerCase()}`,
            { defaultValue: booking.bookingStatus },
        );

        const paymentStatus = paymentStatusCell(booking, details, t, nv);

        const total = details?.payment?.total != null
            ? formatTotal(details.payment.total, language)
            : nv;

        const amountPaid = details?.payment?.amountPaid != null
            ? formatTotal(details.payment.amountPaid, language)
            : nv;

        const balance = isCoveredByOrganization(booking)
            ? na
            : formatOptionalTotal(details?.payment?.balance, nv, language);

        const discount = details?.payment?.couponApplied != null
            ? formatTotal(details.payment.couponApplied, language)
            : nv;

        const invoiceNumber = invoiceNumberCell(booking, details, na, nv);

        const eventType = columns.eventType ? eventTypeCell(details, t, nv) : na;

        const numberOfGuests = columns.numberOfGuests ? numberOfGuestsCell(details, nv) : na;

        const comment = details?.comment || nv;

        const hasIssue =
            (booking.damageReports?.length ?? 0) > 0
                ? t('booking_details.status.yes')
                : t('booking_details.status.no');

        return [
            booking.bookingNumber,
            invoiceNumber,
            serviceLabel,
            eventType,
            booking.startDate ?? '',
            booking.endDate ?? '',
            address,
            booking.senderFullname || nv,
            email,
            phone,
            company,
            customerAddress,
            isRoom ? na : (booking.door != null ? booking.door.toString() : nv),
            numberOfGuests,
            bookingStatus,
            paymentStatus,
            total,
            amountPaid,
            balance,
            discount,
            hasIssue,
            comment,
            booking.createdAt ?? '',
        ].map(escapeCSVField);
    });

    const csvContent = [
        headers.map(escapeCSVField).join(';'),
        ...rows.map((row) => row.join(';')),
    ].join('\n');

    downloadCsv(buildFileName(options, language), csvContent);

    return { failedDetailCount: failedCount, totalCount: filteredByDate.length };
};
